'use client';

import * as React from 'react';
import {
  PenToolIcon,
  SparklesIcon,
  DownloadIcon,
  VideoIcon,
  MusicIcon,
  ShareIcon,
  ClockIcon,
  StarIcon,
  ArrowRightIcon
} from 'lucide-react';

export function HowItWorks(): React.JSX.Element {
  const steps = [
    {
      number: '1',
      title: 'Describe your vision',
      description: 'Write a prompt or upload a script. Be as detailed or as simple as you like. Our AI understands context and emotion.',
    },
    {
      number: '2',
      title: 'AI creates your video',
      description: 'Watch as our AI system generates scenes, adds music, applies effects, and creates a cohesive story.',
    },
    {
      number: '3',
      title: 'Export and share',
      description: 'Download in any format, optimized for any platform. From social media to cinema screens, your video is ready.',
    },
  ];

  const examples = [
    {
      type: 'music-video',
      icon: VideoIcon,
      iconColor: 'text-[#00C6FF]',
      bgColor: 'bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20',
      title: 'Music Video',
      prompt: 'Create an energetic music video with neon visuals, dancers in a warehouse, and dynamic camera movements.',
      result: 'Billboard-ready quality',
      workflow: 'Created in 3 minutes with synchronized choreography, visual effects, and cinematic transitions',
    },
    {
      type: 'commercial',
      icon: SparklesIcon,
      iconColor: 'text-[#4531FF]',
      bgColor: 'bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20',
      title: 'Product Commercial',
      prompt: 'Make a 30-second commercial for eco-friendly sneakers. Show them in nature with upbeat music.',
      result: 'Broadcast quality',
      workflow: 'AI generated product shots, lifestyle scenes, and persuasive messaging in 2 minutes',
    },
    {
      type: 'short-film',
      icon: StarIcon,
      iconColor: 'text-[#00C6FF]',
      bgColor: 'bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20',
      title: 'Cinematic Short',
      prompt: 'Create a 3-minute emotional drama about a letter that arrives 50 years late. Vintage aesthetic.',
      result: 'Festival-ready content',
      workflow: 'Full narrative film created in 5 minutes with period-accurate visuals and emotional score',
    },
  ];

  return (
    <section id="how-it-works" className="py-24 lg:py-32 bg-[#0E0F1A]">
      <div className="container px-6">
        <div className="mx-auto max-w-6xl">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight leading-tight">
              How it works
            </h2>
            <p className="text-lg lg:text-xl text-[#8A8D9B] max-w-2xl mx-auto leading-relaxed">
              From idea to finished video in three simple steps. No production experience needed.
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-12 mb-20">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20 flex items-center justify-center mx-auto mb-4">
                  <span className="text-lg font-medium text-[#00C6FF]">{step.number}</span>
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-2 leading-normal">{step.title}</h3>
                <p className="text-sm text-[#9EA1AE] leading-normal">{step.description}</p>
              </div>
            ))}
          </div>

          {/* Examples */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h3 className="text-2xl lg:text-3xl font-semibold text-white mb-4 leading-snug">Examples of what you can create</h3>
              <p className="text-base text-[#9EA1AE] leading-relaxed">Possibilities for every type of creator</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {examples.map((example, index) => (
                <div key={index} className="group relative overflow-hidden rounded-2xl border border-[#4531FF]/20 bg-[#1A1B29]/80 backdrop-blur-xl hover:border-[#00C6FF]/30 transition-all duration-300">
                  {/* Card Header */}
                  <div className="p-6 pb-4">
                    <div className="mb-4">
                      <div className={`w-10 h-10 rounded-lg ${example.bgColor} flex items-center justify-center`}>
                        <example.icon className={`h-5 w-5 ${example.iconColor}`} />
                      </div>
                    </div>
                    <h4 className="text-lg lg:text-xl font-semibold text-white mb-2 leading-normal">{example.title}</h4>
                    <p className="text-sm text-[#9EA1AE] mb-4 leading-normal">{example.workflow}</p>
                  </div>

                  {/* Prompt Example */}
                  <div className="px-6 pb-4">
                    <p className="text-xs font-medium text-[#9EA1AE] mb-2">USER PROMPT</p>
                    <div className="bg-white/5 rounded-lg p-3 border border-dashed border-white/20">
                      <p className="text-xs font-mono text-white/90">{example.prompt}</p>
                    </div>
                  </div>

                  {/* Result */}
                  <div className="px-6 pb-6">
                    <div className="flex items-center gap-2 text-sm font-medium text-[#00C6FF]">
                      <ClockIcon className="h-4 w-4" />
                      <span>{example.result}</span>
                    </div>
                  </div>

                  {/* Type indicator */}
                  <div className="absolute bottom-4 right-4">
                    <span className="text-[10px] font-medium text-[#9EA1AE] uppercase tracking-wider bg-[#0E0F1A]/80 px-2 py-1 rounded">
                      {example.type.replace('-', ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}